import { useEffect, useRef, useState } from "react";
import { useChatStore, Message } from "@/stores/chat.store";
import { useAuthStore } from "@/stores/auth.store";
import { format, isToday, isYesterday, isSameDay } from "date-fns";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

// Export Message type for reuse
export type { Message };

// Props for external/portal usage
interface ExternalDataProps {
  messages?: Message[];
  loading?: boolean;
  hasMore?: boolean;
  currentUserId?: string;
  onLoadMore?: () => void;
  onReaction?: (messageId: string, emoji: string, hasReacted: boolean) => void;
  typingUsers?: { userName: string }[];
  showReactions?: boolean;
  showReadStatus?: boolean;
}

interface MessageListProps extends ExternalDataProps {
  conversationId: string;
  onReply?: (message: Message) => void;
  /** When "direct", avatars are hidden entirely */
  conversationType?: string;
}

export function MessageList({
  conversationId,
  onReply,
  conversationType,
  // External data props (for portal usage)
  messages: externalMessages,
  loading: externalLoading,
  hasMore: externalHasMore,
  currentUserId: externalUserId,
  onLoadMore,
  onReaction,
  typingUsers: externalTypingUsers,
  showReactions = true,
  showReadStatus = true,
}: MessageListProps) {
  // Use external data if provided, otherwise use internal store
  const useExternalData = externalMessages !== undefined;

  const chatStore = useChatStore();
  const authStore = useAuthStore();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);

  // Resolve data from props or store
  const conversationMessages = useExternalData
    ? externalMessages
    : chatStore.messages.get(conversationId) || [];
  const canLoadMore = useExternalData
    ? (externalHasMore ?? false)
    : (chatStore.hasMoreMessages.get(conversationId) ?? false);
  const messagesLoading = useExternalData
    ? (externalLoading ?? false)
    : chatStore.messagesLoading;
  const currentUserId = useExternalData ? externalUserId : authStore.user?.id;
  const typingRaw = useExternalData
    ? externalTypingUsers || []
    : chatStore.typingUsers.get(conversationId) || [];
  // Never show the current user's own typing indicator
  const typing = typingRaw.filter((u) => (u as any).userId !== currentUserId);

  const handleLoadMore = () => {
    if (useExternalData && onLoadMore) {
      onLoadMore();
    } else if (!useExternalData) {
      chatStore.fetchMessages(conversationId, true);
    }
  };

  const handleReaction = (
    messageId: string,
    emoji: string,
    hasReacted: boolean,
  ) => {
    if (useExternalData && onReaction) {
      onReaction(messageId, emoji, hasReacted);
    } else if (!useExternalData) {
      if (hasReacted) {
        chatStore.removeReaction(messageId, emoji);
      } else {
        chatStore.addReaction(messageId, emoji);
      }
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversationMessages.length]);

  const handleScroll = () => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    setShowScrollButton(!isNearBottom);

    if (scrollTop === 0 && !messagesLoading && canLoadMore) {
      handleLoadMore();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const getInitials = (name: string) => {
    const safe = (name || "?").trim();
    return safe
      .split(" ")
      .map((n) => n[0] ?? "")
      .filter(Boolean)
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?";
  };

  const getSenderName = (sender: Message["sender"] | undefined) => {
    if (!sender) return "?";
    return (
      sender.displayName?.trim() ||
      (sender as any).firstName ||
      sender.userId?.slice(-4) ||
      "?"
    );
  };

  const formatDateSeparator = (date: Date): string => {
    if (isToday(date)) return "Today";
    if (isYesterday(date)) return "Yesterday";
    return format(date, "MMMM d, yyyy");
  };

  const shouldShowDateSeparator = (
    message: Message,
    index: number,
  ): boolean => {
    if (index === 0) return true;
    const prevMessage = conversationMessages[index - 1];
    return !isSameDay(
      new Date(message.createdAt),
      new Date(prevMessage.createdAt),
    );
  };

  const renderDateSeparator = (date: Date) => (
    <div className="flex items-center justify-center my-4">
      <div className="bg-gray-100 text-gray-500 text-xs font-medium px-3 py-1 rounded-full">
        {formatDateSeparator(date)}
      </div>
    </div>
  );

  const renderMessage = (message: Message, index: number) => {
    // Convert to strings for comparison (handles ObjectId objects)
    const senderUserId =
      typeof message.sender.userId === "object"
        ? String(message.sender.userId)
        : message.sender.userId;
    const currentUserIdStr =
      typeof currentUserId === "object" ? String(currentUserId) : currentUserId;

    const isOwn = senderUserId === currentUserIdStr;
    // In direct (1-on-1) chats, never show avatars — only show in group/channel convos
    const isDirect = conversationType === "direct";
    const showAvatar =
      !isDirect &&
      (index === 0 ||
        conversationMessages[index - 1]?.sender.userId !==
          message.sender.userId ||
        shouldShowDateSeparator(message, index));
    const showTimestamp =
      index === conversationMessages.length - 1 ||
      conversationMessages[index + 1]?.sender.userId !== message.sender.userId;
    const isHovered = hoveredMessageId === message._id;

    if (message.type === "system") {
      return (
        <div key={message._id} className="flex justify-center my-2">
          <div className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
            {message.content}
          </div>
        </div>
      );
    }

    return (
      <div key={message._id}>
        {shouldShowDateSeparator(message, index) &&
          renderDateSeparator(new Date(message.createdAt))}

        <div
          className={cn(
            "flex gap-2 mb-1 group relative items-end",
            isOwn ? "flex-row-reverse" : "flex-row",
            !isDirect && !showAvatar && (isOwn ? "mr-10" : "ml-10"),
          )}
          onMouseEnter={() => setHoveredMessageId(message._id)}
          onMouseLeave={() => setHoveredMessageId(null)}
        >
          {showAvatar && message.sender && (
            <Avatar className="h-8 w-8 flex-shrink-0">
              <AvatarImage
                src={message.sender?.avatar}
                alt={getSenderName(message.sender)}
              />
              <AvatarFallback className="text-xs bg-gradient-to-br from-blue-400 to-indigo-500 text-white">
                {getInitials(getSenderName(message.sender))}
              </AvatarFallback>
            </Avatar>
          )}

          <div
            className={cn(
              "max-w-[70%] flex flex-col",
              isOwn ? "items-end" : "items-start",
            )}
          >
            {/* Reply preview */}
            {message.replyTo && (
              <div
                className={cn(
                  "text-xs p-2 rounded-t mb-0.5 border-l-2",
                  isOwn
                    ? "bg-blue-100 border-blue-400"
                    : "bg-gray-100 border-gray-400",
                )}
              >
                <span className="font-medium">
                  {message.replyTo.sender?.displayName || "Unknown"}
                </span>
                <p className="truncate">{message.replyTo.content}</p>
              </div>
            )}

            {/* Message bubble */}
            <div
              className={cn(
                "px-3 py-2 rounded-lg relative",
                isOwn ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-900",
                message.isDeleted && "italic opacity-60",
              )}
            >
              <p className="text-sm whitespace-pre-wrap break-words">
                {message.isDeleted
                  ? "This message was deleted"
                  : message.content}
              </p>

              {/* Attachments */}
              {message.attachments &&
                message.attachments.length > 0 &&
                !message.isDeleted && (
                  <div className="mt-2 space-y-1">
                    {message.attachments.map((attachment, i) =>
                      attachment.type === "image" ? (
                        <img
                          key={i}
                          src={attachment.url}
                          alt={attachment.name}
                          className="max-w-full rounded cursor-pointer hover:opacity-90"
                          onClick={() => window.open(attachment.url, "_blank")}
                        />
                      ) : (
                        <a
                          key={i}
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn(
                            "flex items-center gap-2 text-xs underline",
                            isOwn ? "text-blue-100" : "text-blue-600",
                          )}
                        >
                          📎 {attachment.name}
                        </a>
                      ),
                    )}
                  </div>
                )}

              {/* Message actions */}
              {isHovered && !message.isDeleted && (
                <div
                  className={cn(
                    "absolute top-0 flex items-center gap-1 bg-white shadow-md rounded-lg p-1",
                    isOwn ? "right-full mr-2" : "left-full ml-2",
                  )}
                >
                  {onReply && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => onReply(message)}
                      title="Reply"
                    >
                      <svg
                        className="h-4 w-4 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                        />
                      </svg>
                    </Button>
                  )}
                  {showReactions && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => {
                        const hasReacted = message.reactions?.some(
                          (r) => r.emoji === "👍" && r.userId === currentUserId,
                        );
                        handleReaction(message._id, "👍", hasReacted || false);
                      }}
                      title="Like"
                    >
                      <span className="text-sm">👍</span>
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Reactions */}
            {showReactions &&
              message.reactions &&
              message.reactions.length > 0 && (
                <div className="flex gap-1 mt-1">
                  {Object.entries(
                    message.reactions.reduce(
                      (acc, r) => {
                        acc[r.emoji] = (acc[r.emoji] || 0) + 1;
                        return acc;
                      },
                      {} as Record<string, number>,
                    ),
                  ).map(([emoji, count]) => (
                    <button
                      key={emoji}
                      className="text-xs bg-gray-100 hover:bg-gray-200 px-1.5 py-0.5 rounded-full"
                      onClick={() => {
                        const hasReacted = message.reactions?.some(
                          (r) =>
                            r.emoji === emoji && r.userId === currentUserId,
                        );
                        handleReaction(message._id, emoji, hasReacted || false);
                      }}
                    >
                      {emoji} {count}
                    </button>
                  ))}
                </div>
              )}

            {/* Timestamp & status */}
            {showTimestamp && (
              <div className="flex items-center gap-1 mt-1">
                <span className="text-xs text-gray-400">
                  {format(new Date(message.createdAt), "h:mm a")}
                </span>
                {message.isEdited && (
                  <span className="text-xs text-gray-400">(edited)</span>
                )}
                {showReadStatus && isOwn && (
                  <span className="inline-flex items-center leading-none" title={message.status}>
                    {message.status === "sending" && (
                      <span className="text-[11px] text-gray-300">✓</span>
                    )}
                    {message.status === "sent" && (
                      <span className="text-[11px] text-gray-400">✓</span>
                    )}
                    {message.status === "delivered" && (
                      <span className="text-[11px] text-gray-400 tracking-[-3px]">✓✓</span>
                    )}
                    {message.status === "read" && (
                      <span className="text-[11px] text-blue-500 tracking-[-3px]">✓✓</span>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto p-4 space-y-1 relative"
      onScroll={handleScroll}
    >
      {messagesLoading && canLoadMore && (
        <div className="flex justify-center py-2">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400" />
        </div>
      )}

      {conversationMessages.length === 0 && !messagesLoading ? (
        <div className="flex items-center justify-center h-full text-gray-400">
          No messages yet. Start the conversation!
        </div>
      ) : (
        conversationMessages.map(renderMessage)
      )}

      {/* Typing indicator */}
      {typing.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-gray-500 animate-pulse">
          <span>
            {typing.map((u) => u.userName).join(", ")}{" "}
            {typing.length === 1 ? "is" : "are"} typing...
          </span>
        </div>
      )}

      <div ref={messagesEndRef} />

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <button
          onClick={scrollToBottom}
          className="fixed bottom-24 right-8 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 border"
        >
          <svg
            className="h-5 w-5 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
